import React, { useState } from 'react';
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Text,
  Box,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  SelectInput,
  DropdownOverlay,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { ErrorSource, ErrorType, RecordType, TDS_FILING_DATA_QUERY_KEYS } from 'api/tdsFilingData';
import { formatEpochDateTime } from './utils';

const ERROR_SOURCE_OPTIONS: { label: string; value: ErrorSource }[] = [
  { label: 'Generation', value: 'filing_data_generation' },
  { label: 'FVU Validation', value: 'fvu_validation' },
  { label: 'Parse Validation', value: 'parse_validation' },
];

const ERROR_TYPE_OPTIONS: { label: string; value: ErrorType }[] = [
  { label: 'Duplicate PAN', value: 'duplicate_pan' },
  { label: 'Invalid Data', value: 'invalid_data' },
  { label: 'TDS Shortfall', value: 'tds_shortfall' },
  { label: 'TDS Mismatch', value: 'tds_mismatch' },
  { label: 'Parse Error', value: 'parse_error' },
  { label: 'Missing Field', value: 'missing_field' },
  { label: 'Invalid Format', value: 'invalid_format' },
  { label: 'FVU Rejection', value: 'fvu_rejection' },
  { label: 'Orphan Record', value: 'orphan_record' },
  { label: 'Count Mismatch', value: 'count_mismatch' },
  { label: 'Invalid PAN', value: 'invalid_pan' },
  { label: 'Invalid TAN', value: 'invalid_tan' },
];

const RECORD_TYPE_OPTIONS: { label: string; value: RecordType }[] = [
  { label: 'FH', value: 'FH' },
  { label: 'BH', value: 'BH' },
  { label: 'CD', value: 'CD' },
  { label: 'DD', value: 'DD' },
  { label: 'SD', value: 'SD' },
  { label: 'S16', value: 'S16' },
  { label: 'C6A', value: 'C6A' },
];

interface ErrorTableProps {
  filingId: string;
}

export const ErrorTable: React.FC<ErrorTableProps> = ({ filingId }) => {
  const [page, setPage] = useState(1);
  const [errorSource, setErrorSource] = useState<ErrorSource | undefined>();
  const [errorType, setErrorType] = useState<ErrorType | undefined>();
  const [recordType, setRecordType] = useState<RecordType | undefined>();
  const pageSize = 50;

  // Fetch distinct filter options from the backend — no page_size cap, no redundant data
  const { data: filterOptions } = useQuery({
    queryKey: ['tds-filing-error-filter-options', filingId],
    queryFn: () => api.tdsFilingData.getErrorFilterOptions(filingId),
    staleTime: Infinity, // filter options don't change once fetched for a filing
  });

  const availableSources     = filterOptions?.available_error_sources ?? [];
  const availableTypes       = filterOptions?.available_error_types ?? [];
  const availableRecordTypes = filterOptions?.available_record_types ?? [];

  const { data, isLoading, isError } = useQuery({
    queryKey: [...TDS_FILING_DATA_QUERY_KEYS.errors(filingId), page, errorSource, errorType, recordType],
    queryFn: () =>
      api.tdsFilingData.getErrors(filingId, {
        error_source: errorSource,
        error_type: errorType,
        record_type: recordType,
        page,
        page_size: pageSize,
      }),
  });

  const errors = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;

  // Human-readable labels for dynamically discovered values
  const sourceLabel = (s: ErrorSource) =>
    ({ filing_data_generation: 'Generation', fvu_validation: 'FVU Validation', parse_validation: 'Parse Validation' }[s] ?? s);
  const typeLabel = (t: ErrorType) =>
    t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Box>
      <Box display="flex" gap="spacing.4" marginBottom="spacing.4">
        {availableSources.length > 0 && (
        <Dropdown>
          <SelectInput
            label="Error Source"
            placeholder="All sources"
            value={errorSource}
            onChange={({ values }) => {
              setErrorSource(values[0] as ErrorSource | undefined);
              setPage(1);
            }}
          />
          <DropdownOverlay>
            <ActionList>
              <ActionListItem title="All sources" value="" />
              {availableSources.map((s) => (
                <ActionListItem key={s} title={sourceLabel(s)} value={s} />
              ))}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>
        )}

        <Dropdown>
          <SelectInput
            label="Error Type"
            placeholder="All types"
            value={errorType}
            onChange={({ values }) => {
              setErrorType(values[0] as ErrorType | undefined);
              setPage(1);
            }}
          />
          <DropdownOverlay>
            <ActionList>
              <ActionListItem title="All types" value="" />
              {availableTypes.map((t) => (
                <ActionListItem key={t} title={typeLabel(t)} value={t} />
              ))}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>

        {availableRecordTypes.length > 0 && (
        <Dropdown>
          <SelectInput
            label="Record Type"
            placeholder="All records"
            value={recordType}
            onChange={({ values }) => {
              setRecordType(values[0] as RecordType | undefined);
              setPage(1);
            }}
          />
          <DropdownOverlay>
            <ActionList>
              <ActionListItem title="All records" value="" />
              {availableRecordTypes.map((rt) => (
                <ActionListItem key={rt} title={rt} value={rt} />
              ))}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>
        )}
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" padding="spacing.6">
          <Spinner accessibilityLabel="Loading errors" />
        </Box>
      )}

      {isError && (
        <Alert color="negative" description="Failed to load errors. Please try again." />
      )}

      {!isLoading && !isError && (
        <Table
          data={{ nodes: errors.map((e) => ({ ...e, id: e.error_id })) }}
          pagination={
            <TablePagination
              currentPage={page - 1}
              defaultPageSize={pageSize}
              paginationType="server"
              totalItemCount={totalCount}
              onPageChange={({ page: p }) => setPage(p + 1)}
            />
          }
        >
          {(tableNodes) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Record Type</TableHeaderCell>
                  <TableHeaderCell>Line #</TableHeaderCell>
                  <TableHeaderCell>FVU Code</TableHeaderCell>
                  <TableHeaderCell>FVU Message</TableHeaderCell>
                  <TableHeaderCell>Details</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableNodes.map((error) => {
                  // Build plain-text details from error_details object
                  const details = error.error_details ?? {};
                  const detailText = Object.entries(details)
                    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                    .join(' | ');

                  return (
                  <TableRow key={error.error_id} item={error}>
                    <TableCell>
                      <Badge color="information" size="small">
                        {error.error_source.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge color="negative" size="small">
                        {error.error_type.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text>{error.record_type ?? '—'}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{error.line_number ?? '—'}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{error.fvu_error_code ?? '—'}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{error.fvu_error_message ?? '—'}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{detailText || '—'}</Text>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </>
          )}
        </Table>
      )}
    </Box>
  );
};
